import { Injectable } from '@angular/core';
import { TelematicsService } from '@core/services/telematics.service';
import {
  getPolicyLineSuccess,
  updatePolicyLine,
} from '@core/store/entities/eligible-discounts/eligible-discounts.action';
import { addError } from '@core/store/entities/error/error.action';
import { filterOutNull } from '@shared/rxjs/filter-out-null.operator';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { combineLatest, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { PriceSummaryUtils } from '@shared/utils/hub/price-summary/price-summary.util';
import { ProductsService } from '@core/services/products.service';
import { ProductUtils } from '@app/shared/utils/product.util';
import { updateProduct } from '@entities/product/product.actions';
import { PayBindFormService } from '@forms-store/services/pay-bind-form.service';
import { BillingActions, PayBindActions } from '@core/store/actions';
import { concatLatestFrom } from '@ngrx/operators';
import { PayBindPageModel } from '@core/models/views/pay-bind-page-model';
import { PayBindService } from '@core/services/pay-bind.service';
import { paymentPlanChangedDueToTelematics } from './pay-bind.action';
@Injectable({
  providedIn: 'root',
})
export class PayBindEffects {
  constructor(
    private actions$: Actions,
    private telematicsService: TelematicsService,
    private productsService: ProductsService,
    private payBindService: PayBindService,
    private payBindFormService: PayBindFormService
  ) {}

  /**
   * After getPolicyLineSuccess, look for the BillingPaymentMethod discount.
   * If there isn't one, or it agrees with our billing form, great.
   * Otherwise either:
   *  - Change the policy discount, if user has seen the billing form. (form is the authority)
   *  - Change the billing form, if she has not seen it yet. (backend is the authority)
   * Also, if we receive it with no value (eg after changing effective date), then our form is the authority.
   */
  updatePaymentMethodFormOrDiscountsOnServiceResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPolicyLineSuccess),
      map((action) =>
        action.response.find(
          (d) => d.eligibleDiscountId === 'BillingPaymentMethod'
        )
      ),
      filterOutNull(),
      concatLatestFrom((discount) => [
        this.productsService.getProduct(discount.productType || 'PersonalAuto'),
        this.productsService.getBillingPlansFormVisited(
          discount.productType || 'PersonalAuto'
        ),
      ]),
      map(([discount, product, visited]) => ({
        discount,
        product,
        currentSelection:
          PriceSummaryUtils.paymentMethodDiscountValueForBillingPlan(
            product?.paymentPlan || ''
          ),
        visited,
      })),
      filter(
        ({ discount, currentSelection }) =>
          discount.selectedOptionValue !== currentSelection
      ),
      switchMap(({ discount, product, currentSelection, visited }) => {
        const nextActions: Action[] = [];
        if (!visited && !discount.selectedOptionValue) {
          nextActions.push(
            updatePolicyLine({
              entity: {
                ...discount,
                selectedOptionValue: 'FullPay',
              },
            })
          );
          nextActions.push(
            updateProduct({
              payload: {
                id: product?.type || 'PersonalAuto',
                changes: {
                  paymentPlan:
                    PriceSummaryUtils.billingPlanForPaymentMethodDiscountValue(
                      'FullPay'
                    ),
                },
              },
            })
          );
          nextActions.push(updateProduct);
        } else if (visited || !discount.selectedOptionValue) {
          nextActions.push(
            updatePolicyLine({
              entity: {
                ...discount,
                selectedOptionValue: currentSelection,
              },
            })
          );
        } else {
          nextActions.push(
            updateProduct({
              payload: {
                id: product?.type || 'PersonalAuto',
                changes: {
                  paymentPlan:
                    PriceSummaryUtils.billingPlanForPaymentMethodDiscountValue(
                      discount.selectedOptionValue
                    ),
                },
              },
            })
          );
        }
        return nextActions;
      })
    )
  );

  /**
   * After getPolicyLineSuccess, look for the PaidInFull discount.
   * If there isn't one, or it agrees with our billing form, great.
   * Otherwise:
   *  - Change the policy discount AND the billing form if the user has not visited it yet.
   *  - If she has visited the billing form, we hand off responsibility to the form.
   * Also, if we receive it with no value (eg after changing effective date), then our form is the authority.
   */
  updatePaymentMethodFormOrDiscountsOnServiceResponseForPowersports$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(getPolicyLineSuccess),
        map((action) =>
          action.response.find((d) => d.eligibleDiscountId === 'PaidInFull')
        ),
        filterOutNull(),
        concatLatestFrom((discount) => [
          this.productsService.getProduct(
            discount.productType || 'PersonalAuto'
          ),
          this.productsService.getBillingPlansFormVisited(
            discount.productType || 'PersonalAuto'
          ),
          this.productsService.getSelectedProductTypes(),
        ]),
        filter(([discount, product, visited, productTypes]) =>
          productTypes.some((pType) =>
            ProductUtils.isPowersportsProductType(pType)
          )
        ),
        map(([discount, product, visited, productTypes]) => ({
          discount,
          product,
          currentSelection:
            PriceSummaryUtils.paymentMethodDiscountValueForBillingPlan(
              product?.paymentPlan || ''
            ),
          visited,
          productTypes,
        })),
        filter(
          ({ discount, currentSelection, productTypes }) =>
            discount.selectedOptionValue !== currentSelection
        ),
        switchMap(
          ({ discount, product, currentSelection, visited, productTypes }) => {
            const nextActions: Action[] = [];
            if (!visited) {
              // if selectedOptionValue is false:
              if (discount.selectedOptionValue === 'false') {
                // set the payment method and apply the discount
                nextActions.push(
                  updateProduct({
                    payload: {
                      id: product?.type || 'PersonalAuto',
                      changes: { paymentPlan: 'PAY IN FULL' },
                    },
                  })
                );
                nextActions.push(
                  updatePolicyLine({
                    entity: {
                      ...discount,
                      selectedOptionValue: 'true',
                    },
                  })
                );
              }
            }

            return nextActions;
          }
        )
      )
    );

  forceRecurringBillingForSmartMiles$ = createEffect(() =>
    this.telematicsService.isSmartMilesEnrolled().pipe(
      distinctUntilChanged(),
      filter((enrolled) => enrolled),
      withLatestFrom(
        this.productsService.getProduct('PersonalAuto'),
        this.telematicsService.isTelematicsDeclined()
      ),
      filter(([enrolled, autoProduct, isTelematicsDeclined]) => {
        return (
          !!autoProduct &&
          autoProduct.paymentPlan !== 'MONTHLY EFT' &&
          autoProduct.paymentPlan !== 'MONTHLY RECURRING BANKCARD' &&
          !isTelematicsDeclined
        );
      }),
      switchMap(([enrolled, autoProduct]) => {
        const completed = new Subject<string>();
        if (!!autoProduct) {
          this.payBindFormService
            .orchestratePaymentPlanChangeWithSpinner(
              'MONTHLY EFT',
              autoProduct,
              false
            )
            .subscribe({
              complete: () => {
                completed.next(autoProduct.paymentPlan || '');
              },
            });
        }
        return completed;
      }),
      map((billingPlan) =>
        paymentPlanChangedDueToTelematics({
          previous: billingPlan,
          paymentPlan: 'MONTHLY EFT',
        })
      )
    )
  );

  generateToastWhenPaymentPlanChangesAutomatically$ = createEffect(() =>
    this.actions$.pipe(
      ofType(paymentPlanChangedDueToTelematics),
      withLatestFrom(
        this.productsService.getBillingPlansFormVisited('PersonalAuto')
      ),
      filter(([action, visited]) => visited),
      map((action) =>
        addError({
          payload: {
            displayMessage:
              'We have changed the billing method to allow for SmartMiles.',
          },
        })
      )
    )
  );

  updatePayBindPageModelWhenDownPaymentChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadDownPaymentSuccess),
      switchMap(() =>
        combineLatest([
          this.productsService.getSelectedProducts(),
          this.payBindService.getPaymentTypes(),
        ]).pipe(take(1))
      ),
      mergeMap(([products, paymentTypes]) => {
        const payBindPageModel = {
          products: products || [],
          paymentTypes: paymentTypes || [],
        } as PayBindPageModel;
        return of(
          PayBindActions.updatePayBindPage({
            payload: payBindPageModel,
          })
        );
      })
    )
  );
}
